import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Shield, User, Phone, Eye, EyeOff, KeyRound } from "lucide-react";
import api from "../lib/api";

const Login = () => {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState("admin");
  const { login } = useAuth();

  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: enter identifier, 2: enter OTP & new password
  const [resetIdentifier, setResetIdentifier] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const identifier = isAdmin ? email : phone;
      await login(identifier, password);
      toast.success("Welcome back!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetIdentifier.trim()) {
      toast.error(isAdmin ? "Please enter your email" : "Please enter your mobile number");
      return;
    }

    setResetLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { identifier: resetIdentifier });
      toast.success(res.data.message);
      // Show OTP in development mode
      if (res.data.debug_otp) {
        toast.info(`Debug OTP: ${res.data.debug_otp}`, { duration: 10000 });
      }
      setResetStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetOtp || resetOtp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setResetLoading(true);
    try {
      await api.post("/auth/reset-password", {
        identifier: resetIdentifier,
        otp: resetOtp,
        new_password: newPassword
      });
      toast.success("Password reset successfully! You can now login.");
      closeForgotPassword();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setForgotPasswordOpen(false);
    setResetStep(1);
    setResetIdentifier("");
    setResetOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const isAdmin = loginMode === "admin";

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 mesh-gradient relative overflow-hidden">
        <div className="absolute inset-0 subtle-glow"></div>
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          <div className="max-w-md text-center">
            <div className="mb-8">
              <h1 className="text-5xl font-bold text-slate-800 font-heading tracking-tight">
                Tribe<span className="text-primary">HR</span>
              </h1>
              <p className="text-slate-600 mt-2 text-lg">Human Resource Management</p>
            </div>
            
            <img 
              src="https://images.unsplash.com/photo-1763615445633-c05153e7d1a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxzb2Z0JTIwYWJzdHJhY3QlMjAzRCUyMHNoYXBlcyUyMHBhc3RlbHxlbnwwfHx8fDE3NzIwOTQ4OTh8MA&ixlib=rb-4.1.0&q=85"
              alt="Abstract shapes"
              className="w-full max-w-sm mx-auto rounded-3xl shadow-2xl"
            />
            
            <p className="text-slate-500 mt-8 text-base leading-relaxed">
              Streamline your HR operations with our modern, intuitive platform designed for growing businesses.
            </p>
          </div>
        </div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-800 font-heading">
              Tribe<span className="text-primary">HR</span>
            </h1>
          </div>
          
          <div className="glass rounded-3xl p-8 shadow-xl">
            {/* Mode Toggle */}
            <div className="flex rounded-full bg-slate-100 p-1 mb-6" data-testid="login-mode-toggle">
              <button
                type="button"
                onClick={() => setLoginMode("admin")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium transition-all ${
                  isAdmin ? "bg-white shadow-sm text-primary" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="admin-login-tab"
              >
                <Shield className="h-4 w-4" />
                Admin / HR
              </button>
              <button
                type="button"
                onClick={() => setLoginMode("employee")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium transition-all ${
                  !isAdmin ? "bg-white shadow-sm text-primary" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="employee-login-tab"
              >
                <User className="h-4 w-4" />
                Employee
              </button>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-slate-800 font-heading">
                {isAdmin ? "Admin Sign In" : "Employee Sign In"}
              </h2>
              <p className="text-slate-500 mt-1 text-sm">
                {isAdmin ? "Manage your business HR" : "Access your self-service portal"}
              </p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="identifier" className="text-slate-700 font-medium">
                  {isAdmin ? "Email" : "Mobile Number"}
                </Label>
                <div className="relative">
                  {isAdmin ? (
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                  ) : (
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                  )}
                  <Input
                    id="identifier"
                    type={isAdmin ? "email" : "tel"}
                    placeholder={isAdmin ? "you@company.com" : "+91 98765 43210"}
                    value={isAdmin ? email : phone}
                    onChange={(e) => isAdmin ? setEmail(e.target.value) : setPhone(e.target.value)}
                    className="input-field w-full"
                    style={{ paddingLeft: '3rem' }}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                  <button
                    type="button"
                    onClick={() => {
                      setResetIdentifier(isAdmin ? email : phone);
                      setForgotPasswordOpen(true);
                    }}
                    className="text-sm text-primary hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field w-full"
                    style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-primary h-12 text-base"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Sign In <ArrowRight className="h-5 w-5" />
                  </span>
                )}
              </Button>
            </form>
            
            {isAdmin ? (
              <p className="text-center mt-6 text-slate-500 text-sm">
                New admin?{" "}
                <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
                  Register your business
                </Link>
              </p>
            ) : (
              <p className="text-center mt-6 text-slate-400 text-xs">
                Employee accounts are created by your company admin.
                <br />If you don't have credentials, contact your HR department.
                <br />Use your mobile number and password to sign in.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onOpenChange={closeForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {resetStep === 1 
                ? `Enter your ${isAdmin ? 'email' : 'mobile number'} to receive a password reset OTP.`
                : "Enter the OTP and your new password."
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {resetStep === 1 ? (
              <>
                <div className="space-y-2">
                  <Label>{isAdmin ? "Email" : "Mobile Number"}</Label>
                  <div className="relative">
                    {isAdmin ? (
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    ) : (
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    )}
                    <Input
                      type={isAdmin ? "email" : "tel"}
                      value={resetIdentifier}
                      onChange={(e) => setResetIdentifier(e.target.value)}
                      placeholder={isAdmin ? "you@company.com" : "+91 98765 43210"}
                      className="pl-10"
                      data-testid="reset-identifier-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleForgotPassword} 
                  className="w-full btn-primary"
                  disabled={resetLoading}
                  data-testid="send-reset-otp-btn"
                >
                  {resetLoading ? "Sending..." : "Send OTP"}
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>OTP</Label>
                  <Input
                    type="text"
                    value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="text-center text-lg tracking-widest"
                    data-testid="reset-otp-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setResetStep(1)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleResetPassword} 
                    className="flex-1 btn-primary"
                    disabled={resetLoading}
                    data-testid="reset-password-btn"
                  >
                    {resetLoading ? "Resetting..." : "Reset Password"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
